/**
 * @module UploadReceiptModalStaged
 * @purpose The staged-transport half of the upload modal (probe-aware
 *   steering, spec FR-staged): probes on file pick, and once the analyst has
 *   copied the file to the staging directory, registers it and hands the
 *   returned `operationId` to the shared OperationProgress facility. Also
 *   owns the retry path: a receipt whose staged verification terminates in
 *   `failed` stays re-triggerable via `reverifyStagedReceipt` -- fixing the
 *   original gap where the only "retry" re-ran registration from scratch.
 * @depends services/evidence-containers (probe/register/reverify),
 *   services/collection-receipts (getReceipt, for the uniform
 *   onSuccess(receipt) contract), operation-progress.helpers (reading the
 *   failure cause out of `outcomeSummary`)
 * @dependedOnBy use-upload-receipt-modal
 *
 * @rules
 * - State: local useState; mirrors the shape of the existing upload sub-flow
 * - Errors: TARGET_ALREADY_RECEIVED / evidence_not_received reuse the same
 *   codes as the HTTP path (both wrap `CollectionReceiptError`); reverify's
 *   own conflict codes (VERIFICATION_IN_PROGRESS / STAGING_UNAVAILABLE /
 *   RECEIPT_ALREADY_VERIFIED) and register's refusals (INVALID_FILENAME /
 *   STAGED_PATH_REFUSED / STAGED_FILE_NOT_FOUND / STAGING_UNAVAILABLE) get
 *   their own inline messages -- never a raw code reaches the analyst
 */

import { useCallback, useState } from 'react'
import type { TFunction } from 'i18next'
import { ApiError } from '../../../services/api'
import { getReceipt } from '../../../services/collection-receipts'
import { registerStagedReceipt, reverifyStagedReceipt } from '../../../services/evidence-containers'
import type { CollectionReceipt } from '../../../types/collection-receipts'
import type { CollectionTarget } from '../../../types/collection-targets'
import type { OperationProgress } from '../../../types/evidence-containers'
import { summariseOutcome } from '../../common/operation-progress/operation-progress.helpers'

const TARGET_ALREADY_RECEIVED_CODE = 'TARGET_ALREADY_RECEIVED'
const EVIDENCE_NOT_RECEIVED_CODE = 'evidence_not_received'
const VERIFICATION_IN_PROGRESS_CODE = 'VERIFICATION_IN_PROGRESS'
const STAGING_UNAVAILABLE_CODE = 'STAGING_UNAVAILABLE'
const RECEIPT_ALREADY_VERIFIED_CODE = 'RECEIPT_ALREADY_VERIFIED'
const INVALID_FILENAME_CODE = 'INVALID_FILENAME'
const STAGED_PATH_REFUSED_CODE = 'STAGED_PATH_REFUSED'
const STAGED_FILE_NOT_FOUND_CODE = 'STAGED_FILE_NOT_FOUND'

export type StagedStep = 'idle' | 'registering' | 'verifying' | 'error' | 'verifyFailed'

export interface RegisterStagedArgs {
  caseId: string
  requestId: string
  target: CollectionTarget
  fileName: string
  oobHash: string
  notes: string
}

export interface UseStagedRegistrationResult {
  step: StagedStep
  operationId: string | null
  error: string | null
  alreadyExists: boolean
  register: (args: RegisterStagedArgs) => Promise<void>
  handleVerifyComplete: (progress: OperationProgress) => Promise<void>
  retryVerification: () => Promise<void>
  reset: () => void
}

/** Register refusals with their own inline message (BACKLOG F12). */
function registerRefusalMessage(code: string | undefined, t: TFunction<'cases'>): string | null {
  switch (code) {
    case INVALID_FILENAME_CODE:
      return t('detail.collections.uploadReceipt.stagingInvalidFileName')
    case STAGED_PATH_REFUSED_CODE:
      return t('detail.collections.uploadReceipt.stagingPathRefused')
    case STAGED_FILE_NOT_FOUND_CODE:
      return t('detail.collections.uploadReceipt.stagingFileNotFound')
    case STAGING_UNAVAILABLE_CODE:
      return t('detail.collections.uploadReceipt.reverifyStagingUnavailable')
    default:
      return null
  }
}

function mapStagedError(err: unknown, t: TFunction<'cases'>): { message: string; alreadyExists: boolean } {
  if (err instanceof ApiError) {
    const code = err.body?.error
    if (code === TARGET_ALREADY_RECEIVED_CODE) {
      return { message: t('detail.collections.uploadReceipt.alreadyExists'), alreadyExists: true }
    }
    if (code === EVIDENCE_NOT_RECEIVED_CODE) {
      return { message: t('detail.collections.uploadReceipt.receiveFirstWarning'), alreadyExists: false }
    }
    const refusal = registerRefusalMessage(code, t)
    if (refusal) {
      return { message: refusal, alreadyExists: false }
    }
    return { message: err.body?.message ?? err.message, alreadyExists: false }
  }
  const message = err instanceof Error ? err.message : t('detail.collections.uploadReceipt.stagingRegisterError')
  return { message, alreadyExists: false }
}

function mapReverifyError(err: unknown, t: TFunction<'cases'>): string {
  if (err instanceof ApiError) {
    const code = err.body?.error
    if (code === VERIFICATION_IN_PROGRESS_CODE) {
      return t('detail.collections.uploadReceipt.reverifyInProgress')
    }
    if (code === STAGING_UNAVAILABLE_CODE) {
      return t('detail.collections.uploadReceipt.reverifyStagingUnavailable')
    }
    if (code === RECEIPT_ALREADY_VERIFIED_CODE) {
      return t('detail.collections.uploadReceipt.reverifyAlreadyVerified')
    }
    return err.body?.message ?? err.message
  }
  return err instanceof Error ? err.message : t('detail.collections.uploadReceipt.stagingRegisterError')
}

function readFailureCause(progress: OperationProgress, t: TFunction<'cases'>): string {
  const summary = summariseOutcome(progress.outcomeSummary)
  return summary.primary ?? t('detail.collections.uploadReceipt.stagingRegisterError')
}

interface RegisterDeps {
  args: RegisterStagedArgs
  t: TFunction<'cases'>
  setStep: (step: StagedStep) => void
  setError: (error: string | null) => void
  setAlreadyExists: (value: boolean) => void
  setReceiptId: (id: string | null) => void
  setCaseId: (id: string | null) => void
  setOperationId: (id: string | null) => void
}

async function performRegister(deps: RegisterDeps): Promise<void> {
  const { args, t, setStep, setError, setAlreadyExists, setReceiptId, setCaseId, setOperationId } = deps
  const { caseId, requestId, target, fileName, oobHash, notes } = args
  setStep('registering')
  setError(null)
  setAlreadyExists(false)
  try {
    const result = await registerStagedReceipt(caseId, requestId, {
      targetId: target.id,
      fileName,
      ...(oobHash ? { oobSha256: oobHash } : {}),
      ...(notes ? { notes } : {}),
    })
    setReceiptId(result.receiptId)
    setCaseId(caseId)
    setOperationId(result.operationId)
    setStep('verifying')
  } catch (err) {
    const mapped = mapStagedError(err, t)
    setError(mapped.message)
    setAlreadyExists(mapped.alreadyExists)
    setStep('error')
  }
}

interface RetryVerificationDeps {
  caseId: string | null
  receiptId: string | null
  t: TFunction<'cases'>
  setStep: (step: StagedStep) => void
  setError: (error: string | null) => void
  setOperationId: (id: string | null) => void
}

async function performRetryVerification(deps: RetryVerificationDeps): Promise<void> {
  const { caseId, receiptId, t, setStep, setError, setOperationId } = deps
  if (!caseId || !receiptId) return
  setError(null)
  try {
    const result = await reverifyStagedReceipt(caseId, receiptId)
    setOperationId(result.operationId)
    setStep('verifying')
  } catch (err) {
    setError(mapReverifyError(err, t))
    setStep('verifyFailed')
  }
}

/**
 * @behaviour `register` calls register-staged and moves to `verifying` with
 *   the returned `operationId`; the caller renders OperationProgress and
 *   forwards its terminal result to `handleVerifyComplete`, which fetches
 *   the full receipt on success (preserving the modal's `onSuccess(receipt)`
 *   contract) or surfaces the failure otherwise.
 * @errors Never throws -- failures land in `error`/`alreadyExists`.
 */
export function useStagedRegistration(
  onSuccess: (receipt: CollectionReceipt) => void,
  t: TFunction<'cases'>
): UseStagedRegistrationResult {
  const [step, setStep] = useState<StagedStep>('idle')
  const [operationId, setOperationId] = useState<string | null>(null)
  const [receiptId, setReceiptId] = useState<string | null>(null)
  const [caseId, setCaseId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [alreadyExists, setAlreadyExists] = useState(false)

  const register = useCallback(
    (args: RegisterStagedArgs) =>
      performRegister({
        args,
        t,
        setStep,
        setError,
        setAlreadyExists,
        setReceiptId,
        setCaseId,
        setOperationId,
      }),
    [t]
  )

  const handleVerifyComplete = useCallback(
    async (progress: OperationProgress) => {
      if (progress.status === 'succeeded' && receiptId) {
        const caseIdFromReceipt = progress.caseId
        if (!caseIdFromReceipt) return
        const receipt = await getReceipt(caseIdFromReceipt, receiptId)
        onSuccess(receipt)
        return
      }
      if (progress.status === 'failed') {
        setError(readFailureCause(progress, t))
        setStep('verifyFailed')
        return
      }
      setError(t('detail.collections.uploadReceipt.stagingRegisterError'))
      setStep('error')
    },
    [receiptId, onSuccess, t]
  )

  const retryVerification = useCallback(
    () => performRetryVerification({ caseId, receiptId, t, setStep, setError, setOperationId }),
    [caseId, receiptId, t]
  )

  const reset = useCallback(() => {
    setStep('idle')
    setOperationId(null)
    setReceiptId(null)
    setCaseId(null)
    setError(null)
    setAlreadyExists(false)
  }, [])

  return {
    step,
    operationId,
    error,
    alreadyExists,
    register,
    handleVerifyComplete,
    retryVerification,
    reset,
  }
}
